import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { candidateService, fileUploadService } from '../../services';
import { getProvinces, getDistricts, getAllDistricts } from '../../constants/nepalLocations';

const CandidateFormPage = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { id: paramId } = useParams();
    const { isAdmin, isCandidate, currentUserFullDetails } = useAuth();

    // If admin, use paramId. If candidate, use linked candidateId.
    const id = paramId || (isCandidate() && currentUserFullDetails?.candidateId);
    const isEditMode = !!id;

    const [loading, setLoading] = useState(false);
    const [fetching, setFetching] = useState(isEditMode);
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    // Initialize tab from URL or default
    const queryParams = new URLSearchParams(location.search);
    const initialTab = queryParams.get('tab');
    const [activeTab, setActiveTab] = useState(initialTab || 'basic');

    const [showPassword, setShowPassword] = useState(false);

    const tabsRef = useRef(null);

    // Auto-scroll on tab change
    useEffect(() => {
        if (tabsRef.current) {
            // Scroll to the tabs container so user sees the new content
            tabsRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });

            // Also scroll the active tab button into center view if it's overflowing (mobile)
            const activeTabButton = tabsRef.current.querySelector(`button[data-tab="${activeTab}"]`);
            if (activeTabButton) {
                activeTabButton.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
            }
        } else {
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    }, [activeTab]);

    // Expenditure State
    const [expenditures, setExpenditures] = useState([]);
    const [loadingExpenditures, setLoadingExpenditures] = useState(false);
    const [expenditureForm, setExpenditureForm] = useState({
        title: '',
        description: '',
        images: []
    });
    const [expenditureFiles, setExpenditureFiles] = useState([]);
    const [expenditurePreview, setExpenditurePreview] = useState([]);

    const [formData, setFormData] = useState({
        // Common/Current Fields
        name: '',
        constituency: '',
        district: '',
        bio: '',

        photoKey: '',
        province: '',
        isEnabled: true,

        // Reference Specific Fields
        date_of_birth: '',
        mobile_no: '',

        email: '',
        password: '',
        permanent_address: '',
        temporary_address: '',
        educational_qualification: '',
        expertise_area: '',
        current_job: '',
        current_job_other: '',
        electorate: '',
        area_of_interest: '',
        years_of_experience: '',
        major_contributions: ['', '', ''],
        involved_in_political_party: false,
        political_party_relation: '',
        contribution_in_revolution: '',
        social_contribution: '',
        politics_before: '',
        previous_election: '',
        details_of_last_election: '',
        votes_received: '',
        self_evaluation_on_team: '',
        main_problem_of_area: '',
        first_problem_to_solve: '',
        self_declaration: '',
        any_conviction: '',
        why_you: '',
        if_other_than_who: '',
        alternative_election_area: '',
        declaration: '',
        ethnicity: '',
        summary: '',
        personalWebsite: '',
        issues_and_solutions: [{ issue: '', solution: '' }],
        campaign_schedule: [{ date: '', event: '', location: '' }],
    });

    const [availableDistricts, setAvailableDistricts] = useState(getAllDistricts());
    const provinces = getProvinces();
    const constituencyOptions = Array.from({ length: 15 }, (_, i) => i + 1);

    // Update available districts when province changes in formData
    useEffect(() => {
        if (formData.province) {
            const provinceDistricts = getDistricts(formData.province);
            setAvailableDistricts(provinceDistricts);
        } else {
            setAvailableDistricts(getAllDistricts());
        }
    }, [formData.province]);

    const { refreshProfile } = useAuth();

    const [photoFile, setPhotoFile] = useState(null);
    const [photoPreview, setPhotoPreview] = useState(null);

    // Initial Data Fetching for Edit Mode
    useEffect(() => {
        if (isEditMode) {
            fetchCandidateData();
            fetchExpenditures();
        } else {
            // Reset form if not in edit mode (e.g. creating new or switched user)
            resetForm();
        }
    }, [id]);

    const resetForm = () => {
        setFormData({
            name: '',
            constituency: '',
            district: '',
            bio: '',

            photoKey: '',
            province: '',
            isEnabled: true,
            date_of_birth: '',
            mobile_no: '',
            email: '',
            password: '',
            permanent_address: '',
            temporary_address: '',
            educational_qualification: '',
            expertise_area: '',
            current_job: '',
            current_job_other: '',
            electorate: '',
            area_of_interest: '',
            years_of_experience: '',
            major_contributions: ['', '', ''],
            involved_in_political_party: false,
            political_party_relation: '',
            contribution_in_revolution: '',
            social_contribution: '',
            politics_before: '',
            previous_election: '',
            details_of_last_election: '',
            votes_received: '',
            self_evaluation_on_team: '',
            main_problem_of_area: '',
            first_problem_to_solve: '',
            self_declaration: '',
            any_conviction: '',
            why_you: '',
            if_other_than_who: '',
            alternative_election_area: '',
            declaration: '',
            ethnicity: '',
            summary: '',
            personalWebsite: '',
            issues_and_solutions: [{ issue: '', solution: '' }],
            campaign_schedule: [{ date: '', event: '', location: '' }],
        });
        setExpenditures([]);
        setPhotoPreview(null);
        setPhotoFile(null);
        setError('');
        setSuccess('');
    };

    const fetchExpenditures = async () => {
        if (!id) return;
        setLoadingExpenditures(true);
        try {
            const res = await candidateService.getExpenditures(id);
            setExpenditures(res?.data || res || []);
        } catch (err) {
            console.error(err);
        } finally {
            setLoadingExpenditures(false);
        }
    };

    const fetchCandidateData = async () => {
        setFetching(true);
        setError('');
        try {
            const response = await candidateService.getCandidate(id);
            const data = response.data || response;

            setFormData({
                name: data.name || '',
                constituency: data.constituency || '',
                district: data.district || '',
                bio: data.bio || '',

                photoKey: data.photoKey || '',
                province: data.province || '',
                isEnabled: data.isEnabled !== undefined ? data.isEnabled : true,

                date_of_birth: data.dateOfBirth ? data.dateOfBirth.split('T')[0] : '',
                mobile_no: data.mobileNo || '',
                email: data.email || '',
                password: '', // Never populate password
                permanent_address: data.permanentAddress || '',
                temporary_address: data.temporaryAddress || '',
                educational_qualification: data.educationalQualification || '',
                expertise_area: data.expertiseArea || '',
                current_job: data.currentJob || '',
                current_job_other: data.currentJobOther || '',
                electorate: data.constituency || '',
                area_of_interest: data.areaOfInterest || '',
                years_of_experience: data.yearsOfExperience || '',
                major_contributions: Array.isArray(data.majorContributions)
                    ? [data.majorContributions[0] || '', data.majorContributions[1] || '', data.majorContributions[2] || '']
                    : ['', '', ''],
                involved_in_political_party: data.involvedInPoliticalParty || false,
                political_party_relation: data.politicalPartyRelation || '',
                contribution_in_revolution: data.contributionInRevolution || '',
                social_contribution: data.socialContribution || '',
                politics_before: data.politicsBefore || '',
                previous_election: data.previousElection || '',
                details_of_last_election: data.detailsOfLastElection || '',
                votes_received: data.votesReceived || '',
                self_evaluation_on_team: data.selfEvaluationOnTeam || '',
                main_problem_of_area: data.mainProblemOfArea || '',
                first_problem_to_solve: data.firstProblemToSolve || '',
                self_declaration: data.selfDeclaration || '',
                any_conviction: data.anyConviction || '',
                why_you: data.whyYou || '',
                if_other_than_who: data.ifOtherThanWho || '',
                alternative_election_area: data.alternativeElectionArea || '',
                declaration: data.declaration || '',
                ethnicity: data.ethnicity || '',
                summary: data.summary || '',
                personalWebsite: data.personalWebsite || '',
                issues_and_solutions: data.issuesAndSolutions || [{ issue: '', solution: '' }],
                campaign_schedule: (data.campaignSchedule && data.campaignSchedule.length > 0)
                    ? data.campaignSchedule.map(item => ({
                        date: item.date || '',
                        event: item.event || item.place || '',
                        location: item.location || ''
                    }))
                    : [{ date: '', event: '', location: '' }],
            });

            if (data.photoKeyUrl) {
                setPhotoPreview(data.photoKeyUrl);
            }
        } catch (err) {
            setError('Failed to load candidate data');
            console.error(err);
        } finally {
            setFetching(false);
        }
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleContributionChange = (index, value) => {
        const newContributions = [...formData.major_contributions];
        newContributions[index] = value;
        setFormData(prev => ({ ...prev, major_contributions: newContributions }));
    };

    const handlePhotoChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setPhotoFile(file);
            const reader = new FileReader();
            reader.onloadend = () => {
                setPhotoPreview(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };



    // Expenditure Handlers
    const handleExpenditureChange = (e) => {
        const { name, value } = e.target;
        setExpenditureForm(prev => ({ ...prev, [name]: value }));
    };

    const handleExpenditureFilesChange = (e) => {
        const files = Array.from(e.target.files);
        setExpenditureFiles(files);

        // Previews
        const previews = files.map(file => URL.createObjectURL(file));
        setExpenditurePreview(previews);
    };

    const handleExpenditureSubmit = async (e) => {
        e.preventDefault();
        setLoading(true); // Reuse main loading or create specific
        try {
            const imageKeys = [];
            if (expenditureFiles.length > 0) {
                for (const file of expenditureFiles) {
                    const uploadRes = await fileUploadService.uploadFile(file);
                    const key = uploadRes.key || uploadRes.data?.key || uploadRes.url || uploadRes.filePath || uploadRes.data?.filePath;
                    if (key) {
                        imageKeys.push(key);
                    }
                }
            }

            const payload = {
                ...expenditureForm,
                images: imageKeys
            };

            await candidateService.createExpenditure(id, payload);
            setSuccess('Expenditure added successfully');

            // Reset form
            setExpenditureForm({ title: '', description: '', images: [] });
            setExpenditureFiles([]);
            setExpenditurePreview([]);

            // Refresh list
            fetchExpenditures();
        } catch (err) {
            console.error('Failed to add expenditure', err);
            setError('Failed to add expenditure');
        } finally {
            setLoading(false);
        }
    };
    const handleIssueChange = (index, field, value) => {
        const newItems = [...formData.issues_and_solutions];
        if (!newItems[index]) newItems[index] = { issue: '', solution: '' };
        newItems[index][field] = value;
        setFormData(prev => ({ ...prev, issues_and_solutions: newItems }));
    };
    const addIssue = () => {
        setFormData(prev => ({ ...prev, issues_and_solutions: [...prev.issues_and_solutions, { issue: '', solution: '' }] }));
    };
    const removeIssue = (index) => {
        setFormData(prev => ({ ...prev, issues_and_solutions: prev.issues_and_solutions.filter((_, i) => i !== index) }));
    };

    const handleScheduleChange = (index, field, value) => {
        const newItems = [...formData.campaign_schedule];
        if (!newItems[index]) newItems[index] = { date: '', event: '', location: '' };
        newItems[index][field] = value;
        setFormData(prev => ({ ...prev, campaign_schedule: newItems }));
    };
    const addSchedule = () => {
        setFormData(prev => ({ ...prev, campaign_schedule: [...prev.campaign_schedule, { date: '', event: '', location: '' }] }));
    };
    const removeSchedule = (index) => {
        setFormData(prev => ({ ...prev, campaign_schedule: prev.campaign_schedule.filter((_, i) => i !== index) }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');
        setSuccess('');

        try {
            let photoKey = formData.photoKey;

            // Upload photo if new file selected
            if (photoFile) {
                setUploading(true);
                try {
                    const uploadResponse = await fileUploadService.uploadFile(photoFile, 'webp');
                    photoKey = uploadResponse.data?.key || uploadResponse.key || uploadResponse.data?.filePath || uploadResponse.filePath;
                } catch (uploadErr) {
                    console.error("Photo upload failed", uploadErr);
                    throw new Error('Failed to upload photo');
                } finally {
                    setUploading(false);
                }
            }

            // Prepare Payload for Local App (Source of Truth)
            const payload = {
                name: formData.name,
                constituency: formData.constituency,
                district: formData.district,
                province: formData.province,
                bio: formData.bio,

                isEnabled: formData.isEnabled,

                // Profile fields (camelCase for DTO)
                dateOfBirth: formData.date_of_birth || null,
                mobileNo: formData.mobile_no || '',
                email: formData.email || '',
                permanentAddress: formData.permanent_address || '',
                temporaryAddress: formData.temporary_address || '',
                educationalQualification: formData.educational_qualification || '',
                expertiseArea: formData.expertise_area || '',
                currentJob: formData.current_job || '',
                currentJobOther: formData.current_job_other || '',
                areaOfInterest: formData.area_of_interest || '',
                yearsOfExperience: parseInt(formData.years_of_experience) || 0,
                majorContributions: formData.major_contributions.filter(c => c.trim() !== ''),
                involvedInPoliticalParty: formData.involved_in_political_party,
                politicalPartyRelation: formData.political_party_relation || '',
                contributionInRevolution: formData.contribution_in_revolution || '',
                socialContribution: formData.social_contribution || '',
                politicsBefore: formData.politics_before || '',
                previousElection: formData.previous_election || '',
                detailsOfLastElection: formData.details_of_last_election || '',
                votesReceived: parseInt(formData.votes_received) || 0,
                selfEvaluationOnTeam: formData.self_evaluation_on_team || '',
                mainProblemOfArea: formData.main_problem_of_area || '',
                firstProblemToSolve: formData.first_problem_to_solve || '',
                selfDeclaration: formData.self_declaration || '',
                anyConviction: formData.any_conviction || '',
                whyYou: formData.why_you || '',
                ifOtherThanWho: formData.if_other_than_who || '',
                alternativeElectionArea: formData.alternative_election_area || '',
                declaration: formData.declaration || '',
                ethnicity: formData.ethnicity || '',
                summary: formData.summary || '',
                personalWebsite: formData.personalWebsite || '',
                issuesAndSolutions: formData.issues_and_solutions,
                campaignSchedule: formData.campaign_schedule,
            };

            if (photoKey) payload.photoKey = photoKey;
            if (formData.password) payload.password = formData.password;

            if (isEditMode) {
                await candidateService.updateCandidate(id, payload);
                // If the user is the candidate updating themselves, refresh the global auth/user state
                if (isCandidate()) {
                    await refreshProfile();
                }
                setSuccess('Candidate updated successfully!');
            } else {
                await candidateService.createCandidate(payload);
                setSuccess('Candidate created successfully!');
            }

            if (isAdmin()) {
                setTimeout(() => navigate('/admin/candidates'), 1500);
            } else {
                // For candidates, just scroll to top or stay
                window.scrollTo(0, 0);
            }

        } catch (err) {
            console.error(err);
            setError(err.response?.data?.message || err.message || 'Failed to save candidate');
        } finally {
            setLoading(false);
        }
    };

    if (fetching) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 py-8">
            <div className="max-w-4xl mx-auto px-4">
                <div className="mb-8">
                    {isAdmin() && (
                        <button
                            onClick={() => navigate('/admin/candidates')}
                            className="mb-4 inline-flex items-center text-sm text-gray-500 hover:text-gray-700 transition-colors"
                        >
                            <svg className="w-5 h-5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                            </svg>
                            Back to Candidates
                        </button>
                    )}
                    <h1 className="text-3xl font-bold text-gray-900 mb-2">{isEditMode ? 'Edit Candidate' : 'Add New Candidate'}</h1>
                    <p className="text-gray-600">{isEditMode ? 'Update candidate details across platforms.' : 'Create a new candidate profile across platforms.'}</p>
                </div>

                {error && <div className="mb-4 p-4 bg-red-50 text-red-700 rounded-lg border border-red-200">{error}</div>}
                {success && <div className="mb-4 p-4 bg-green-50 text-green-700 rounded-lg border border-green-200">{success}</div>}

                {/* Tabs */}
                <div ref={tabsRef} className="mb-6 border-b border-gray-200">
                    <nav className="-mb-px flex space-x-8 overflow-x-auto pb-2 scrollbar-hide">
                        {['basic', 'extended', 'political', 'contributions', 'campaign', 'additional', ...(isEditMode ? ['expenditure'] : [])].map((tab) => (
                            <button
                                key={tab}
                                data-tab={tab}
                                onClick={() => setActiveTab(tab)}
                                className={`${activeTab === tab
                                    ? 'border-blue-500 text-blue-600'
                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                    } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm capitalize`}
                            >
                                {tab} Info
                            </button>
                        ))}
                    </nav>
                </div>

                <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow-md p-6">

                    {/* Basic Info Tab */}
                    {activeTab === 'basic' && (
                        <div className="space-y-6 animate-fadeIn">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Candidate Photo</label>
                                <div className="flex items-center gap-6">
                                    {photoPreview ? (
                                        <img src={photoPreview} alt="Preview" className="h-24 w-24 rounded-full object-cover border-2 border-gray-200" />
                                    ) : (
                                        <div className="h-24 w-24 rounded-full bg-gray-100 flex items-center justify-center text-gray-400">
                                            <span className="text-xs">No Photo</span>
                                        </div>
                                    )}
                                    <label className="cursor-pointer bg-white py-2 px-3 border border-gray-300 rounded-md shadow-sm text-sm leading-4 font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500">
                                        <span>Change Photo</span>
                                        <input type="file" className="hidden" accept="image/*" onChange={handlePhotoChange} />
                                    </label>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Full Name *</label>
                                    <input type="text" name="name" value={formData.name} onChange={handleChange} required className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500" />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Province (Pradesh) *</label>
                                    <select
                                        name="province"
                                        value={formData.province}
                                        onChange={handleChange}
                                        required
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 shadow-sm"
                                    >
                                        <option value="">Select Province</option>
                                        {provinces.map(p => (
                                            <option key={p} value={p}>{p}</option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">District *</label>
                                    <select
                                        name="district"
                                        value={formData.district}
                                        onChange={handleChange}
                                        required
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 shadow-sm"
                                    >
                                        <option value="">Select District</option>
                                        {availableDistricts.map(d => (
                                            <option key={d} value={d}>{d}</option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Constituency *</label>
                                    <select
                                        name="constituency"
                                        value={formData.constituency}
                                        onChange={handleChange}
                                        required
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 shadow-sm"
                                    >
                                        <option value="">Select Constituency</option>
                                        {constituencyOptions.map(num => (
                                            <option key={num} value={num.toString()}>{num}</option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Date of Birth</label>
                                    <input type="date" name="date_of_birth" value={formData.date_of_birth} onChange={handleChange} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500" />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Mobile No</label>
                                    <input type="tel" name="mobile_no" value={formData.mobile_no} onChange={handleChange} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500" />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                                    <input type="email" name="email" value={formData.email} onChange={handleChange} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500" />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
                                    <div className="relative">
                                        <input
                                            type={showPassword ? 'text' : 'password'}
                                            name="password"
                                            value={formData.password}
                                            onChange={handleChange}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                                            placeholder={isEditMode ? 'Leave blank to keep unchanged' : ''}
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowPassword(!showPassword)}
                                            className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                                        >
                                            {showPassword ? (
                                                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                                </svg>
                                            ) : (
                                                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l18 18" />
                                                </svg>
                                            )}
                                        </button>
                                    </div>
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Biography *</label>
                                <textarea name="bio" value={formData.bio} onChange={handleChange} required rows={4} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"></textarea>
                            </div>

                            <div className="flex items-center">
                                <input type="checkbox" id="isEnabled" name="isEnabled" checked={formData.isEnabled} onChange={handleChange} className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" />
                                <label htmlFor="isEnabled" className="ml-2 block text-sm text-gray-900">Enable Candidate (Visible Publicly)</label>
                            </div>
                        </div>
                    )}

                    {/* Extended Info Tab */}
                    {activeTab === 'extended' && (
                        <div className="space-y-6 animate-fadeIn">
                            <div className="grid grid-cols-1 gap-6">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Permanent Address</label>
                                    <textarea name="permanent_address" value={formData.permanent_address} onChange={handleChange} rows={2} className="w-full px-3 py-2 border border-gray-300 rounded-md" />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Temporary Address</label>
                                    <textarea name="temporary_address" value={formData.temporary_address} onChange={handleChange} rows={2} className="w-full px-3 py-2 border border-gray-300 rounded-md" />
                                </div>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Educational Qualification</label>
                                    <input type="text" name="educational_qualification" value={formData.educational_qualification} onChange={handleChange} className="w-full px-3 py-2 border border-gray-300 rounded-md" />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Expertise Area</label>
                                    <input type="text" name="expertise_area" value={formData.expertise_area} onChange={handleChange} className="w-full px-3 py-2 border border-gray-300 rounded-md" />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Current Job</label>
                                    <input type="text" name="current_job" value={formData.current_job} onChange={handleChange} className="w-full px-3 py-2 border border-gray-300 rounded-md" />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Years of Experience</label>
                                    <input type="number" name="years_of_experience" value={formData.years_of_experience} onChange={handleChange} className="w-full px-3 py-2 border border-gray-300 rounded-md" />
                                </div>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Area of Interest</label>
                                <input type="text" name="area_of_interest" value={formData.area_of_interest} onChange={handleChange} className="w-full px-3 py-2 border border-gray-300 rounded-md" />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Personal Website / Link (Optional)</label>
                                <input type="url" name="personalWebsite" value={formData.personalWebsite} onChange={handleChange} placeholder="https://example.com" className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500" />
                            </div>
                        </div>
                    )}

                    {/* Political Info Tab */}
                    {activeTab === 'political' && (
                        <div className="space-y-6 animate-fadeIn">
                            <div className="flex items-center mb-4">
                                <input type="checkbox" name="involved_in_political_party" checked={formData.involved_in_political_party} onChange={handleChange} className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" />
                                <label className="ml-2 block text-sm text-gray-900">Involved in Political Party</label>
                            </div>
                            {formData.involved_in_political_party && (
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Political Party Relation</label>
                                    <input type="text" name="political_party_relation" value={formData.political_party_relation} onChange={handleChange} className="w-full px-3 py-2 border border-gray-300 rounded-md" />
                                </div>
                            )}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Contribution in Revolution</label>
                                <textarea name="contribution_in_revolution" value={formData.contribution_in_revolution} onChange={handleChange} rows={3} className="w-full px-3 py-2 border border-gray-300 rounded-md" />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Previous Election Details</label>
                                <textarea name="previous_election" value={formData.previous_election} onChange={handleChange} rows={2} className="w-full px-3 py-2 border border-gray-300 rounded-md" placeholder="Where, when.." />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Votes Received (if any)</label>
                                <input type="number" name="votes_received" value={formData.votes_received} onChange={handleChange} className="w-full px-3 py-2 border border-gray-300 rounded-md" />
                            </div>
                        </div>
                    )}

                    {/* Contributions Tab */}
                    {activeTab === 'contributions' && (
                        <div className="space-y-6 animate-fadeIn">
                            <h3 className="text-lg font-medium text-gray-900">Major Contributions (Top 3)</h3>
                            {[0, 1, 2].map(index => (
                                <div key={index}>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Contribution {index + 1}</label>
                                    <input type="text" value={formData.major_contributions[index]} onChange={(e) => handleContributionChange(index, e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-md" />
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Campaign Tab */}
                    {activeTab === 'campaign' && (
                        <div className="space-y-8 animate-fadeIn">
                            {/* Summary */}
                            <div>
                                <h3 className="text-lg font-medium text-gray-900 border-b pb-2 mb-4">Candidate Summary</h3>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Executive Summary</label>
                                <textarea
                                    name="summary"
                                    value={formData.summary}
                                    onChange={handleChange}
                                    rows={4}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                                    placeholder="Brief summary of the candidate's campaign and vision..."
                                />
                            </div>

                            {/* Issues & Solutions */}
                            <div>
                                <div className="flex justify-between items-center border-b pb-2 mb-4">
                                    <h3 className="text-lg font-medium text-gray-900">Issues & Solutions</h3>
                                    <button type="button" onClick={addIssue} className="text-sm bg-blue-50 text-blue-600 px-3 py-1 rounded hover:bg-blue-100 flex items-center">
                                        <span className="mr-1">+</span> Add Issue
                                    </button>
                                </div>
                                <div className="space-y-4">
                                    {formData.issues_and_solutions.map((item, index) => (
                                        <div key={index} className="flex gap-4 items-start bg-gray-50 p-4 rounded-lg border border-gray-200">
                                            <div className="flex-1 space-y-4">
                                                <div>
                                                    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Issue</label>
                                                    <input
                                                        type="text"
                                                        value={item.issue}
                                                        onChange={(e) => handleIssueChange(index, 'issue', e.target.value)}
                                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                                                        placeholder="e.g. Traffic Congestion"
                                                    />
                                                </div>
                                                <div>
                                                    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Proposed Solution</label>
                                                    <textarea
                                                        value={item.solution}
                                                        onChange={(e) => handleIssueChange(index, 'solution', e.target.value)}
                                                        rows={2}
                                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                                                        placeholder="e.g. Implement smart traffic lights..."
                                                    />
                                                </div>
                                            </div>
                                            <button
                                                type="button"
                                                onClick={() => removeIssue(index)}
                                                className="text-red-500 hover:text-red-700 p-1 mt-6"
                                                title="Remove"
                                            >
                                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                                            </button>
                                        </div>
                                    ))}
                                    {formData.issues_and_solutions.length === 0 && (
                                        <div className="text-center py-4 text-gray-500 bg-gray-50 rounded-lg border border-dashed border-gray-300">
                                            No issues added yet. Click "Add Issue" to start.
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Schedule */}
                            <div>
                                <div className="flex justify-between items-center border-b pb-2 mb-4">
                                    <h3 className="text-lg font-medium text-gray-900">Campaign Schedule</h3>
                                    <button type="button" onClick={addSchedule} className="text-sm bg-blue-50 text-blue-600 px-3 py-1 rounded hover:bg-blue-100 flex items-center">
                                        <span className="mr-1">+</span> Add Event
                                    </button>
                                </div>
                                <div className="space-y-4">
                                    {formData.campaign_schedule.map((item, index) => (
                                        <div key={index} className="flex gap-4 items-start bg-gray-50 p-4 rounded-lg border border-gray-200">
                                            <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-4">
                                                <div>
                                                    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Date</label>
                                                    <input
                                                        type="date"
                                                        value={item.date}
                                                        onChange={(e) => handleScheduleChange(index, 'date', e.target.value)}
                                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                                                    />
                                                </div>
                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                    <div>
                                                        <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Event</label>
                                                        <input
                                                            type="text"
                                                            value={item.event || ''}
                                                            onChange={(e) => handleScheduleChange(index, 'event', e.target.value)}
                                                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                                                            placeholder="e.g. Town Hall Meeting"
                                                        />
                                                    </div>
                                                    <div>
                                                        <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Location</label>
                                                        <input
                                                            type="text"
                                                            value={item.location || ''}
                                                            onChange={(e) => handleScheduleChange(index, 'location', e.target.value)}
                                                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                                                            placeholder="e.g. Kathmandu"
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                            <button
                                                type="button"
                                                onClick={() => removeSchedule(index)}
                                                className="text-red-500 hover:text-red-700 p-1 mt-6"
                                                title="Remove"
                                            >
                                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                                            </button>
                                        </div>
                                    ))}
                                    {formData.campaign_schedule.length === 0 && (
                                        <div className="text-center py-4 text-gray-500 bg-gray-50 rounded-lg border border-dashed border-gray-300">
                                            No schedule events added. Click "Add Event" to start.
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Additional Info Tab */}
                    {activeTab === 'additional' && (
                        <div className="space-y-6 animate-fadeIn">
                            {['self_evaluation_on_team', 'main_problem_of_area', 'first_problem_to_solve', 'why_you', 'declaration'].map(field => (
                                <div key={field}>
                                    <label className="block text-sm font-medium text-gray-700 mb-1 capitalize">{field.replace(/_/g, ' ')}</label>
                                    <textarea name={field} value={formData[field]} onChange={handleChange} rows={3} className="w-full px-3 py-2 border border-gray-300 rounded-md" />
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Expenditure Tab */}
                    {activeTab === 'expenditure' && (
                        <div className="space-y-8 animate-fadeIn">
                            {/* Add New Expenditure Form */}
                            <div className="bg-gray-50 p-6 rounded-lg border border-gray-200">
                                <h3 className="text-lg font-bold text-gray-900 mb-4">Add New Expenditure</h3>
                                <div className="space-y-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
                                        <input
                                            type="text"
                                            name="title"
                                            value={expenditureForm.title}
                                            onChange={handleExpenditureChange}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                            placeholder="e.g. Campaign Banner Printing"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                                        <textarea
                                            name="description"
                                            value={expenditureForm.description}
                                            onChange={handleExpenditureChange}
                                            rows={3}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                            placeholder="Details of the expenditure..."
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Images (Multiple)</label>
                                        <input
                                            type="file"
                                            multiple
                                            accept="image/*"
                                            onChange={handleExpenditureFilesChange}
                                            className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                                        />
                                        {expenditurePreview.length > 0 && (
                                            <div className="flex gap-2 mt-2 flex-wrap">
                                                {expenditurePreview.map((src, idx) => (
                                                    <img key={idx} src={src} alt="Preview" className="h-16 w-16 object-cover rounded border" />
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                    <div className="flex justify-end">
                                        <button
                                            type="button" // Use button type to prevent firing main form submit
                                            onClick={handleExpenditureSubmit}
                                            disabled={loading || !expenditureForm.title}
                                            className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 disabled:opacity-50"
                                        >
                                            {loading ? 'Adding...' : 'Add Expenditure'}
                                        </button>
                                    </div>
                                </div>
                            </div>

                            {/* List Expenditures */}
                            <div>
                                <h3 className="text-lg font-bold text-gray-900 mb-4">Expenditure History</h3>
                                {loadingExpenditures ? (
                                    <p>Loading...</p>
                                ) : expenditures.length === 0 ? (
                                    <p className="text-gray-500">No expenditures recorded yet.</p>
                                ) : (
                                    <div className="space-y-4">
                                        {expenditures.map((exp) => (
                                            <div key={exp.id} className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
                                                <div className="flex justify-between items-start">
                                                    <div>
                                                        <h4 className="font-bold text-gray-800">{exp.title}</h4>
                                                        {exp.description && <p className="text-sm text-gray-600 mt-1">{exp.description}</p>}
                                                        <p className="text-xs text-gray-400 mt-2">{new Date(exp.createdAt).toLocaleDateString()}</p>
                                                    </div>
                                                </div>
                                                {(exp.imagesUrl || exp.images) && (exp.imagesUrl || exp.images).length > 0 && (
                                                    <div className="mt-3 flex gap-2 overflow-x-auto pb-2">
                                                        {(exp.imagesUrl || exp.images).map((img, idx) => (
                                                            <a key={idx} href={img} target="_blank" rel="noopener noreferrer">
                                                                <img src={img} alt="Expenditure" className="h-20 w-auto object-cover rounded border hover:opacity-80 transition" />
                                                            </a>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Action Buttons */}
                    <div className="mt-8 flex gap-4 pt-4 border-t border-gray-200">
                        {isAdmin() && (
                            <button type="button" onClick={() => navigate('/admin/candidates')} className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium">Cancel</button>
                        )}
                        <button type="submit" disabled={loading || uploading} className="flex-1 bg-blue-600 text-white px-4 py-3 rounded-lg hover:bg-blue-700 disabled:opacity-50 font-medium shadow-sm">
                            {loading || uploading ? 'Processing...' : (isEditMode ? 'Update Candidate' : 'Create Candidate')}
                        </button>
                    </div>

                </form>
            </div>
        </div>
    );
};

export default CandidateFormPage;
